import { FC, JSX } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import type { Slide } from '@player/content';

import { contentApi } from '@/entities/content/api/contentApi';
import { Content } from '@/entities/content/api/types';
import Button from '@/shared/ui/button';
import Input from '@/shared/ui/Input';

import { AutoUpdateSettings } from './AutoUpdateSettings';

import s from './MetadataSettings.module.css';

export interface IRssSettingsProps {}

export const RssSettings: FC<IRssSettingsProps> = (): JSX.Element => {
  const { setValue, control } = useFormContext<Content>();

  const [triggerLoadSlides, slidesFromRssQuery] =
    contentApi.endpoints.loadSlidesFromRss.useLazyQuery();

  const feedUrl = useWatch({ control, name: 'rss_settings.link' });
  const itemsCount = useWatch({ control, name: 'rss_settings.limit' });

  const onLoadClick = async () => {
    try {
      const result = await triggerLoadSlides({
        // Manually encode to avoid breaking the query string when a
        // schema is present.
        url: encodeURIComponent(feedUrl ?? ''),
        limit: typeof itemsCount === 'number' ? itemsCount : undefined,
      }).unwrap();

      const loadedSlides = result?.data as Slide[] | undefined;
      if (loadedSlides?.length) {
        // I believe we can avoid this manual id generation in next version:
        // https://github.com/react-hook-form/react-hook-form/releases/tag/v8.0.0-beta.1
        setValue(
          'slides',
          loadedSlides.map((slide) => ({ ...slide, id: Math.random() }))
        );
      }
    } catch {
      // Error state is surfaced via slidesFromRssQuery.isError below.
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-[12px] bg-[#f8f8f9]/[0.56] p-4 dark:bg-white/[.02]">
      <div className="text-base font-semibold text-black dark:text-white">
        RSS Settings
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex-1">
          <Controller
            control={control}
            name="rss_settings.link"
            render={({ field: { value, onChange } }) => (
              <Input
                className={s.finput}
                placeholder="RSS feed URL"
                type="url"
                value={value}
                onChange={onChange}
              />
            )}
          />
        </div>

        <div className="flex w-full flex-1">
          <Controller
            control={control}
            name="rss_settings.limit"
            render={({ field: { value, onChange } }) => (
              <Input
                className={s.finput}
                placeholder="Slides number"
                type="number"
                min={1}
                value={value}
                onChange={(e) => {
                  const parsed = Number(e.target.value);
                  onChange(Number.isNaN(parsed) ? '' : parsed);
                }}
              />
            )}
          />
        </div>

        <div className="flex w-full items-center">
          <Button
            variant="primary"
            size="md"
            title="Load"
            onClick={onLoadClick}
            isLoading={
              slidesFromRssQuery.isLoading || slidesFromRssQuery.isFetching
            }
            disabled={!feedUrl}
            className="mb-4 w-full"
          />
        </div>

        {slidesFromRssQuery.isError && (
          <div className="text-sm font-light text-red">
            {String(
              (slidesFromRssQuery.error as Error)?.message ||
                'Failed to load slides'
            )}
          </div>
        )}

        <AutoUpdateSettings />
      </div>
    </div>
  );
};

export default RssSettings;
